import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "./api";
import { useOnline } from "./useOnline";
import { flushQueue, getQueue, QUEUE_EVENT } from "./offlineQueue";

const handlers = {
  create_customer: (payload) => api.post("/customers", payload).then((r) => r.data),
  create_rental: (payload) => api.post("/rentals", payload).then((r) => r.data),
  edit_rental: (rentalId, payload) => api.put(`/rentals/${rentalId}/items`, payload).then((r) => r.data),
};

// Central offline-sync engine. Mount once (in Layout).
export function useOfflineSync() {
  const online = useOnline();
  const [pending, setPending] = useState(0);
  const [errors, setErrors] = useState(0);
  const [syncing, setSyncing] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const all = await getQueue();
      setPending(all.filter((r) => r.status !== "error").length);
      setErrors(all.filter((r) => r.status === "error").length);
    } catch (e) { /* noop */ }
  }, []);

  const sync = useCallback(async (silent = false) => {
    if (!navigator.onLine || !localStorage.getItem("rp_token")) return;
    setSyncing(true);
    const res = await flushQueue(handlers);
    setSyncing(false);
    await refresh();
    if (res.synced > 0) {
      if (!silent) toast.success(`${res.synced} perubahan offline berhasil tersinkron`);
      // Auto-clean any accidental duplicates after a sync batch.
      api.post("/rentals/dedupe").catch(() => {});
    }
    if (res.failed > 0 && !silent) toast.error(`${res.failed} perubahan gagal disinkron, akan dicoba lagi`);
    return res;
  }, [refresh]);

  useEffect(() => { refresh(); }, [refresh]);

  useEffect(() => {
    const onChange = () => refresh();
    window.addEventListener(QUEUE_EVENT, onChange);
    return () => window.removeEventListener(QUEUE_EVENT, onChange);
  }, [refresh]);

  useEffect(() => { if (online) sync(true); }, [online, sync]);

  useEffect(() => {
    const id = setInterval(() => { if (navigator.onLine && pending > 0) sync(true); }, 30000);
    return () => clearInterval(id);
  }, [pending, sync]);

  return { online, pending, errors, syncing, sync };
}
